import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Button,
  Card,
  CardActions,
  Stack,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import parseColor from "parse-color";
import { useEffect, useMemo, useState } from "react";
import { useFragment } from "react-relay";
import { RecordSourceSelectorProxy } from "relay-runtime";
import { BrandAppConfigBuildThemeInputPartial } from "../../../../mutations/__generated__/useBrandAppConfigUpdate2Mutation.graphql";
import {
  InternBrandStylePalette_brand$data,
  InternBrandStylePalette_brand$key,
} from "./__generated__/InternBrandStylePalette_brand.graphql";
import useBrandAppConfigUpdate2Mutation from "../../../../mutations/useBrandAppConfigUpdate2Mutation";
import {
  BrandColorPalette,
  getBrandColorToAppColorPalette,
  getBrandToAppColorPalette,
} from "../../../../utils/getColorMapping";
import InternBrandStylePaletteComparison from "./InternBrandStylePaletteComparison";
import InternBrandStylePaletteItem from "./InternBrandStylePaletteItem";
import PaletteComponentsPreviews from "./previews";
import {
  ComponentColors,
  convertBuildThemeToComponentColors,
} from "./previews/types";

const brandFragment = graphql`
  fragment InternBrandStylePalette_brand on BrandType {
    id
    internStyle {
      colorPalettes {
        background
        foreground
        accentBackground
        accentForeground
        adPriority
        emailPriority
        componentColors {
          button {
            background
            foreground
            outline
          }
          shortcuts {
            background
            foreground
            outline
            container
          }
          badge {
            background
            foreground
          }
          navBar {
            background
            foreground
          }
          backgrounds {
            background1
            background2
          }
          onboarding {
            background
            foreground
          }
        }
      }
      primaryButton {
        background
        foreground
        borderColor
      }
      navbar {
        background
        foreground
      }
      suggestedButtonStyle {
        borderRadius
        borderWidth
        textStyle
      }
    }
    appConfig {
      id
      primaryPaletteLight
      accent1PaletteLight
      accent2PaletteLight
      accent3PaletteLight
      background1PaletteLight
      background2PaletteLight

      buildTheme {
        id
        primaryButtonBackground
        primaryButtonForeground
        secondaryButtonOutline
        primaryPillBackground
        primaryPillForeground
        secondaryPillOutline
        pillContainerBackground
        badgeBackground
        badgeForeground
        navigationBackground
        navigationForeground
        onboardingBackground
        onboardingForeground

        buttonBorderThickness
        buttonCornerRadius
        buttonTextStyle
      }
    }
  }
`;

const convertToBrandColorPalette = (
  palette: NonNullable<
    InternBrandStylePalette_brand$data["internStyle"]
  >["colorPalettes"][number]
): BrandColorPalette => {
  return {
    primary: parseColor(palette.foreground).hex,
    accent1: parseColor(palette.background).hex,
    accent2: parseColor(palette.accentForeground).hex,
    accent3: parseColor(palette.accentBackground).hex,
    background1: parseColor(palette.componentColors.backgrounds.background1)
      .hex,
    background2: parseColor(palette.componentColors.backgrounds.background2)
      .hex,
  };
};

type InternBrandStylePaletteProps = {
  brand: InternBrandStylePalette_brand$key;
  onPaletteSelect?: (palette: BrandColorPalette) => void;
};

const InternBrandStylePalette = ({
  brand: brandKey,
  onPaletteSelect,
}: InternBrandStylePaletteProps) => {
  const brand = useFragment(brandFragment, brandKey);
  const [updateBrandAppConfig, isMutationInFlight] =
    useBrandAppConfigUpdate2Mutation();
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedPalette, setSelectedPalette] =
    useState<BrandColorPalette | null>(null);

  const palettes = useMemo(() => {
    if (!brand.internStyle?.colorPalettes) return [];
    return brand.internStyle.colorPalettes.map(convertToBrandColorPalette);
  }, [brand.internStyle?.colorPalettes]);

  const savedPalette: BrandColorPalette | null = useMemo(() => {
    if (!brand.appConfig) return null;
    return {
      primary: brand.appConfig.primaryPaletteLight,
      accent1: brand.appConfig.accent1PaletteLight,
      accent2: brand.appConfig.accent2PaletteLight,
      accent3: brand.appConfig.accent3PaletteLight,
      background1: brand.appConfig.background1PaletteLight,
      background2: brand.appConfig.background2PaletteLight,
    };
  }, [brand.appConfig]);

  const savedPaletteMapping: ComponentColors | null = useMemo(() => {
    if (!brand.appConfig?.buildTheme || !savedPalette) return null;
    return convertBuildThemeToComponentColors(
      brand.appConfig.buildTheme,
      savedPalette
    );
  }, [brand.appConfig?.buildTheme, savedPalette]);

  const selectedPaletteMapping: ComponentColors | null = useMemo(() => {
    const palette = brand.internStyle?.colorPalettes[selectedIndex];
    if (!palette) return null;
    return palette.componentColors;
  }, [selectedIndex, brand.internStyle?.colorPalettes]);

  useEffect(() => {
    if (selectedIndex === null || palettes.length <= selectedIndex) {
      return;
    }
    setSelectedPalette(palettes[selectedIndex]);
  }, [selectedIndex, palettes]);

  const hasChanges = useMemo(() => {
    if (!selectedPalette || !savedPalette) {
      return selectedPalette !== savedPalette;
    }

    return (
      JSON.stringify(selectedPalette) !== JSON.stringify(savedPalette) ||
      JSON.stringify(selectedPaletteMapping) !==
        JSON.stringify(savedPaletteMapping)
    );
  }, [
    selectedPalette,
    savedPalette,
    selectedPaletteMapping,
    savedPaletteMapping,
  ]);

  const handlePaletteSelect = (palette: BrandColorPalette, index: number) => {
    setSelectedIndex(index);
    onPaletteSelect?.(palette);
  };

  const handleSave = () => {
    if (!selectedPalette || !selectedPaletteMapping) return;

    const brandToAppColorPalette =
      getBrandColorToAppColorPalette(selectedPalette);

    const buildThemeValues = {
      primaryButtonBackground: getBrandToAppColorPalette({
        color: selectedPaletteMapping.button.background,
        palette: brandToAppColorPalette,
      }),
      primaryButtonForeground: getBrandToAppColorPalette({
        color: selectedPaletteMapping.button.foreground,
        palette: brandToAppColorPalette,
      }),
      secondaryButtonOutline: getBrandToAppColorPalette({
        color: selectedPaletteMapping.button.outline,
        palette: brandToAppColorPalette,
      }),
      primaryPillBackground: getBrandToAppColorPalette({
        color: selectedPaletteMapping.shortcuts.background,
        palette: brandToAppColorPalette,
      }),
      primaryPillForeground: getBrandToAppColorPalette({
        color: selectedPaletteMapping.shortcuts.foreground,
        palette: brandToAppColorPalette,
      }),
      secondaryPillOutline: getBrandToAppColorPalette({
        color: selectedPaletteMapping.shortcuts.outline,
        palette: brandToAppColorPalette,
      }),
      pillContainerBackground: getBrandToAppColorPalette({
        color: selectedPaletteMapping.shortcuts.container,
        palette: brandToAppColorPalette,
      }),
      badgeBackground: getBrandToAppColorPalette({
        color: selectedPaletteMapping.badge.background,
        palette: brandToAppColorPalette,
      }),
      badgeForeground: getBrandToAppColorPalette({
        color: selectedPaletteMapping.badge.foreground,
        palette: brandToAppColorPalette,
      }),
      navigationBackground: getBrandToAppColorPalette({
        color: selectedPaletteMapping.navBar.background,
        palette: brandToAppColorPalette,
      }),
      navigationForeground: getBrandToAppColorPalette({
        color: selectedPaletteMapping.navBar.foreground,
        palette: brandToAppColorPalette,
      }),
      onboardingBackground: getBrandToAppColorPalette({
        color: selectedPaletteMapping.onboarding.background,
        palette: brandToAppColorPalette,
      }),
      onboardingForeground: getBrandToAppColorPalette({
        color: selectedPaletteMapping.onboarding.foreground,
        palette: brandToAppColorPalette,
      }),
    };

    let buttonStyleValues:
      | Pick<
          BrandAppConfigBuildThemeInputPartial,
          "buttonCornerRadius" | "buttonBorderThickness" | "buttonTextStyle"
        >
      | undefined = undefined;
    if (brand.internStyle?.suggestedButtonStyle) {
      buttonStyleValues = {
        buttonCornerRadius: brand.internStyle.suggestedButtonStyle.borderRadius,
        buttonBorderThickness:
          brand.internStyle.suggestedButtonStyle.borderWidth,
        buttonTextStyle: brand.internStyle.suggestedButtonStyle.textStyle,
      };
    }
    updateBrandAppConfig(
      {
        id: brand.appConfig?.id,
        primaryPaletteLight: selectedPalette.primary,
        accent1PaletteLight: selectedPalette.accent1,
        accent2PaletteLight: selectedPalette.accent2,
        accent3PaletteLight: selectedPalette.accent3,
        background1PaletteLight: selectedPalette.background1,
        background2PaletteLight: selectedPalette.background2,
        buildTheme: {
          id: brand.appConfig?.buildTheme?.id,
          ...buildThemeValues,
          ...buttonStyleValues,
        },
      },
      () => {},
      undefined,
      (store: RecordSourceSelectorProxy) => {
        // Update the appConfig in the Relay cache
        const brandRecord = store.get(brand.id);
        if (brandRecord == null) {
          return;
        }

        const appConfigRecord = brandRecord.getLinkedRecord("appConfig");
        if (appConfigRecord == null) {
          return;
        }

        // Update the palette colors in the cache
        appConfigRecord.setValue(
          selectedPalette.primary,
          "primaryPaletteLight"
        );
        appConfigRecord.setValue(
          selectedPalette.accent1,
          "accent1PaletteLight"
        );
        appConfigRecord.setValue(
          selectedPalette.accent2,
          "accent2PaletteLight"
        );
        appConfigRecord.setValue(
          selectedPalette.accent3,
          "accent3PaletteLight"
        );

        const buildTheme = appConfigRecord.getLinkedRecord("buildTheme");
        if (buildTheme == null) {
          return;
        }

        // Use the pre-calculated values instead of recalculating
        buildTheme.setValue(
          buildThemeValues.primaryButtonBackground,
          "primaryButtonBackground"
        );
        buildTheme.setValue(
          buildThemeValues.primaryButtonForeground,
          "primaryButtonForeground"
        );
        buildTheme.setValue(
          buildThemeValues.secondaryButtonOutline,
          "secondaryButtonOutline"
        );
        buildTheme.setValue(
          buildThemeValues.primaryPillBackground,
          "primaryPillBackground"
        );
        buildTheme.setValue(
          buildThemeValues.primaryPillForeground,
          "primaryPillForeground"
        );
        buildTheme.setValue(
          buildThemeValues.secondaryPillOutline,
          "secondaryPillOutline"
        );
        buildTheme.setValue(
          buildThemeValues.pillContainerBackground,
          "pillContainerBackground"
        );
        buildTheme.setValue(
          buildThemeValues.badgeBackground,
          "badgeBackground"
        );
        buildTheme.setValue(
          buildThemeValues.badgeForeground,
          "badgeForeground"
        );
        buildTheme.setValue(
          buildThemeValues.navigationBackground,
          "navigationBackground"
        );
        buildTheme.setValue(
          buildThemeValues.navigationForeground,
          "navigationForeground"
        );
        buildTheme.setValue(
          buildThemeValues.onboardingBackground,
          "onboardingBackground"
        );
        buildTheme.setValue(
          buildThemeValues.onboardingForeground,
          "onboardingForeground"
        );
        if (buttonStyleValues) {
          buildTheme.setValue(
            buttonStyleValues.buttonCornerRadius,
            "buttonCornerRadius"
          );
          buildTheme.setValue(
            buttonStyleValues.buttonBorderThickness,
            "buttonBorderThickness"
          );
          buildTheme.setValue(
            buttonStyleValues.buttonTextStyle,
            "buttonTextStyle"
          );
        }
      }
    );
  };

  return (
    <Card
      sx={{
        p: 2,
        width: "100%",
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h6" mb={2} fontWeight={600}>
        Palette
      </Typography>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={4}
        sx={{ width: "100%", minWidth: 0, overflowX: "auto", flex: 1 }}
      >
        {/* Left side - Palette list */}
        <Box
          sx={{
            flex: { md: "0 0 300px" },
            width: { xs: "100%", md: 300 },
            maxWidth: "100%",
            position: "relative",
            minHeight: "180px",
          }}
        >
          <Box
            p={1}
            sx={{
              overflowY: "auto",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            {palettes?.map((palette, index) => (
              <InternBrandStylePaletteItem
                key={index}
                index={index}
                palette={palette}
                variant="minimal"
                selectable={true}
                isSelected={index === selectedIndex}
                onClick={() => handlePaletteSelect(palette, index)}
                size="small"
              />
            ))}
          </Box>
        </Box>

        {/* Right side - Selected palette preview */}
        <Stack
          sx={{ flex: "1 1 auto", minWidth: 0 }}
          alignItems="start"
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
        >
          <InternBrandStylePaletteComparison
            originalPalette={savedPalette ?? undefined}
            selectedPalette={selectedPalette ?? undefined}
          />
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ minWidth: 0, maxWidth: "100%", overflowX: "auto" }}
          >
            {savedPaletteMapping && (
              <PaletteComponentsPreviews
                selectedPalette={savedPaletteMapping}
                buttonStyle={{
                  buttonCornerRadius:
                    brand.appConfig?.buildTheme?.buttonCornerRadius ?? 0,
                  buttonBorderThickness:
                    brand.appConfig?.buildTheme?.buttonBorderThickness ?? 0,
                  buttonTextStyle:
                    brand.appConfig?.buildTheme?.buttonTextStyle ?? "UPPERCASE",
                }}
              />
            )}
            {selectedPaletteMapping && (
              <>
                <ArrowForwardIcon fontSize="small" />
                <PaletteComponentsPreviews
                  selectedPalette={selectedPaletteMapping}
                  buttonStyle={{
                    buttonCornerRadius:
                      brand.internStyle?.suggestedButtonStyle?.borderRadius ??
                      0,
                    buttonBorderThickness:
                      brand.internStyle?.suggestedButtonStyle?.borderWidth ?? 0,
                    buttonTextStyle:
                      brand.internStyle?.suggestedButtonStyle?.textStyle ??
                      "UPPERCASE",
                  }}
                />
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
      <CardActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          p: 2,
          mt: "auto",
          borderTop: "1px solid #DBDCDD",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          disabled={!hasChanges || isMutationInFlight}
          onClick={handleSave}
        >
          {isMutationInFlight ? "Saving..." : "Save"}
        </Button>
      </CardActions>
    </Card>
  );
};

export default InternBrandStylePalette;
